import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ChevronDown, ChevronRight, Search, Lock, Package, Layers, LayoutList, GitBranch, Trash2, Sparkles, Link2, BarChart3, Check, XCircle, CalendarCheck, ArrowLeft } from 'lucide-react';
import SWMDecompositionTree from './SWMDecompositionTree';
import SWMCognaraPlanner from './SWMCognaraPlanner';
import SWMManpowerRollup from './SWMManpowerRollup';
import SWMMarkActivityLinker from './SWMMarkActivityLinker';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444', label: 'Unallocated' },
  PARTIAL_SOFT: { bg: '#eab30820', text: '#eab308', label: 'Partial Soft' },
  READY_SOFT: { bg: '#3b82f620', text: '#3b82f6', label: 'Ready Soft' },
  PO_READY: { bg: '#143A6B20', text: '#143A6B', label: 'PO Ready' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b', label: 'Partial Hard' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e', label: 'Fully Allocated' },
};

const FAB_STAGES = ['cuttingAndFitting', 'welding', 'surfaceTreatment', 'dispatch'];
const FAB_LABELS = { cuttingAndFitting: 'Cut & Fit', welding: 'Welding', surfaceTreatment: 'Surface', dispatch: 'Dispatch' };
const EREC_STAGES = ['erection', 'boltTorquing', 'alignment', 'grouting'];
const EREC_LABELS = { erection: 'Erection', boltTorquing: 'Bolt Torq', alignment: 'Alignment', grouting: 'Grouting' };

function fmtWt(v) { return v != null ? `${Number(v).toFixed(1)} kg` : '-'; }
function fmtPct(v) { return v != null ? `${Number(v).toFixed(0)}%` : '0%'; }

function TagMaterialsPanel({ projectId, structuralTag }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/materials`,
        );
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setData({ materials: [] }); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId, structuralTag]);

  if (loading) return (
    <div className="flex items-center justify-center py-3">
      <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--text-muted)' }} />
    </div>
  );
  const mats = data?.materials || [];
  if (!mats.length) return (
    <div className="text-center text-[10px] py-3 italic" style={{ color: 'var(--text-muted)' }}>
      No linked materials · run the STR-demo seed to populate raw-material MCIs.
    </div>
  );
  const currency = data?.currency || 'USD';
  const isBoqDerived = !!data?.derivedFromMarkBoq;
  const fmtVal = (v) => v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const statusColor = (s) => s === 'COMPLETED' ? '#0E7C6B' : s === 'DELAYED' ? '#EF4444' : s === 'AT_RISK' ? '#F59206' : s === 'PLANNED' ? '#94A3B8' : '#3b82f6';
  return (
    <div data-testid={`swm-tag-materials-${structuralTag}`}>
      <div className="px-3 py-1.5 flex items-center gap-3 text-[10px] font-mono border-b flex-wrap" style={{ background: 'rgba(20,58,107,0.06)', borderColor: 'rgba(20,58,107,0.15)', color: '#143A6B' }}>
        <Package className="w-3 h-3" />
        <b>{isBoqDerived ? 'Bill of Quantities for this tag' : 'Materials feeding this tag'}</b>
        {isBoqDerived ? (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: '#F5920620', color: '#F59206' }}
                title="Derived from mark-number BoQ · no MCI PO raised yet"
                data-testid={`swm-tag-materials-boq-badge-${structuralTag}`}>
            BOQ • no MCI PO raised yet
          </span>
        ) : (
          <>
            <span>· {data.mciCount} MCIs</span>
            <span>· PO value {currency} {fmtVal(data.totalPoValue)}</span>
          </>
        )}
        <span>· {data.stockUnits} unit{data.stockUnits === 1 ? '' : 's'} on rack</span>
        {Object.entries(data.stockByUnit || {}).map(([u, v]) => (
          <span key={u} className="px-1 rounded" style={{ background: 'rgba(14,124,107,0.12)', color: '#0E7C6B' }}>{fmtVal(v)} {u}</span>
        ))}
      </div>
      <table className="w-full text-[10px]">
        <thead style={{ background: 'var(--card-bg-alt)' }}>
          <tr style={{ color: 'var(--text-muted)' }}>
            <th className="px-2 py-1 text-left font-medium">Material</th>
            <th className="px-2 py-1 text-left font-medium">Vendor</th>
            <th className="px-2 py-1 text-left font-medium">PO</th>
            <th className="px-2 py-1 text-right font-medium">Qty</th>
            <th className="px-2 py-1 text-right font-medium">Value</th>
            <th className="px-2 py-1 text-center font-medium">GRN</th>
            <th className="px-2 py-1 text-center font-medium">IQC</th>
            <th className="px-2 py-1 text-center font-medium">Stock</th>
            <th className="px-2 py-1 text-center font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {mats.map((m, idx) => (
            <tr key={m.mciLineNumber || `boq-${m.materialTypeCode}-${idx}`} className="border-b" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-tag-material-row-${m.mciLineNumber || m.materialTypeCode}`}>
              <td className="px-2 py-1">
                <div className="font-mono font-medium flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                  {m.materialTypeCode || '-'}
                  {m.sectionProfile && <span className="text-[9px] font-normal px-1 rounded" style={{ background: 'rgba(20,58,107,0.08)', color: '#143A6B' }}>{m.sectionProfile}</span>}
                </div>
                <div className="truncate max-w-[260px]" style={{ color: 'var(--text-muted)' }} title={m.materialDescription}>{m.materialDescription}</div>
                {m.isDerivedFromMarkBoq && m.linkedMarkNumbers?.length > 0 && (
                  <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }} title={m.linkedMarkNumbers.join(', ')}>
                    <span style={{ color: '#F59206' }}>◈</span> {m.linkedMarkNumbers.length} mark{m.linkedMarkNumbers.length === 1 ? '' : 's'} · Hard {m.hardAllocationPercent || 0}% · Soft {m.softAllocationPercent || 0}%
                  </div>
                )}
              </td>
              <td className="px-2 py-1" style={{ color: 'var(--text-primary)' }}>{m.vendor || '-'}</td>
              <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-muted)' }}>
                <div>{m.poNumber || (m.isDerivedFromMarkBoq ? <span className="italic text-[9px]" style={{ color: '#F59206' }}>MR pending</span> : '-')}</div>
                {m.mrNumber && <div className="text-[9px]">MR {m.mrNumber}</div>}
              </td>
              <td className="px-2 py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtVal(m.quantity)} <span style={{ color: 'var(--text-muted)' }}>{m.unit}</span></td>
              <td className="px-2 py-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{m.currency || 'USD'} {fmtVal(m.poValue)}</td>
              <td className="px-2 py-1 text-center">
                {m.grnNumber ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono" style={{ color: '#0E7C6B' }}
                    title={`${m.grnNumber} · ${m.grnActualDate?.slice(0,10) || ''}`}>
                    <Check className="w-2.5 h-2.5" strokeWidth={3} /> {m.grnNumber}
                  </span>
                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
              </td>
              <td className="px-2 py-1 text-center">
                {m.iqcResult === 'PASS' ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono" style={{ color: '#0E7C6B' }} title={m.iqcReportRef}>
                    <Check className="w-2.5 h-2.5" strokeWidth={3} /> PASS
                  </span>
                ) : m.iqcResult === 'FAIL' ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono" style={{ color: '#EF4444' }}>FAIL</span>
                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
              </td>
              <td className="px-2 py-1 text-center font-mono" style={{ color: m.stockOnRack.units ? '#0E7C6B' : 'var(--text-muted)' }}>
                {m.stockOnRack.units ? `${fmtVal(m.stockOnRack.qty)} ${m.unit}` : '—'}
              </td>
              <td className="px-2 py-1 text-center">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{
                  background: `${statusColor(m.overallStatus)}22`, color: statusColor(m.overallStatus),
                }}>{m.overallStatus || m.currentPhase || '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MarkDetailPanel({ projectId, structuralTag }) {
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarks, setSelectedMarks] = useState(new Set());
  const [linkerMode, setLinkerMode] = useState(null); // 'manual' | 'cognara' | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/mark-numbers?structuralTag=${structuralTag}`);
      setMarks(res.data?.markNumbers || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, structuralTag]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <tr><td colSpan={16} className="p-3">
      <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
    </td></tr>
  );

  const toggleMark = (mno) => setSelectedMarks(s => { const n = new Set(s); n.has(mno) ? n.delete(mno) : n.add(mno); return n; });
  const toggleAll = () => setSelectedMarks(s => s.size === marks.length ? new Set() : new Set(marks.map(m => m.markNumber)));

  return (
    <tr data-testid={`mark-detail-${structuralTag}`}>
      <td colSpan={16} style={{ padding: 0 }}>
        <div className="mx-3 my-1.5 rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'rgba(20,58,107,0.25)' }}>
          <TagMaterialsPanel projectId={projectId} structuralTag={structuralTag} />
        </div>
        <div className="mx-3 my-1.5 rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: '#F5920640' }}>
          <div className="px-3 py-1.5 text-[10px] font-semibold border-b flex items-center gap-2" style={{ background: '#F5920608', borderColor: '#F5920620', color: '#F59206' }}>
            <Layers className="w-3 h-3 inline" /> Mark Numbers for {structuralTag} ({marks.length})
            <div className="ml-auto flex items-center gap-2">
              {selectedMarks.size > 0 && (
                <>
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-primary)' }}>{selectedMarks.size} selected</span>
                  <button onClick={() => setLinkerMode('manual')} data-testid={`mark-link-manual-${structuralTag}`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[9.5px] font-medium border"
                    style={{ borderColor: '#143A6B', color: '#143A6B' }}>
                    <Link2 className="w-2.5 h-2.5"/> Manual link
                  </button>
                </>
              )}
              <button onClick={() => setLinkerMode('cognara')} data-testid={`mark-link-cognara-${structuralTag}`}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[9.5px] font-medium text-white"
                style={{ background: '#F26B1F' }}>
                <Sparkles className="w-2.5 h-2.5"/> Cognara mark-link
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ background: 'var(--card-bg-alt)' }}>
                  {['', 'Mark #', 'Activity', 'Section', 'Pcs', 'Weight', 'Alloc %', 'Fab Blocked', 'Cut&Fit', 'Weld', 'Surface', 'Dispatch', 'Erection', 'BoltTorq', 'Align', 'Grout'].map((h, i) => (
                    <th key={h + i} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
                      {i === 0 ? <input type="checkbox" checked={selectedMarks.size === marks.length && marks.length > 0} onChange={toggleAll} data-testid={`mark-select-all-${structuralTag}`}/> : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {marks.map(m => {
                  const fab = m.stageGates?.fabrication || {};
                  const erec = m.stageGates?.erection || {};
                  const allocPct = m.overallAllocatedPercent || 0;
                  return (
                    <tr key={m.markNumber} className="hover:brightness-95 transition-colors" style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td className="px-2 py-1"><input type="checkbox" checked={selectedMarks.has(m.markNumber)} onChange={() => toggleMark(m.markNumber)} data-testid={`mark-checkbox-${m.markNumber}`}/></td>
                      <td className="px-2 py-1 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{m.markNumber}</td>
                      <td className="px-2 py-1 text-[9.5px]" style={{ color: 'var(--text-muted)' }}>
                        {m.linkedMacroActivityName
                          ? (
                            <div className="flex flex-col gap-0.5 max-w-[240px]">
                              <span className="inline-flex items-center gap-1"
                                title={`${(m.linkedMacroActivityWbsPath || []).map(x => `${x.wbs} · ${x.name}`).join('  ›  ') || m.linkedMacroActivityWBS || ''} · ${m.linkedMacroActivityName}`}
                                style={{ color: m.markLinkStatus === 'SUGGESTED' ? '#F26B1F' : m.markLinkStatus === 'APPROVED' ? '#0E7C6B' : 'var(--text-muted)' }}>
                                {m.markLinkSource === 'MANUAL' ? <Link2 className="w-2 h-2 shrink-0"/> : <Sparkles className="w-2 h-2 shrink-0"/>}
                                {(m.linkedMacroActivityWbsPath && m.linkedMacroActivityWbsPath.length > 1) && (
                                  <span className="font-mono text-[8px] truncate" style={{ color: '#94A3B8' }}>
                                    {m.linkedMacroActivityWbsPath.slice(0, -1).map(x => x.name).join(' › ')} ›
                                  </span>
                                )}
                                {m.linkedMacroActivityWBS && (
                                  <span className="font-mono text-[8.5px] px-0.5 rounded shrink-0" style={{ background: 'rgba(20,58,107,0.08)', color: '#143A6B' }}>{m.linkedMacroActivityWBS}</span>
                                )}
                                <span className="truncate">{m.linkedMacroActivityName}</span>
                              </span>
                              {m.cognaraCascade && (m.cognaraCascade.plannedFabStart || m.cognaraCascade.plannedErectStart) && (
                                <div className="flex items-center gap-1 text-[8px] pl-2.5" title="Cognara-cascaded per-mark dates">
                                  {m.cognaraCascade.plannedFabStart && (
                                    <span className="px-0.5 rounded" style={{ background: '#DBEAFE', color: '#1E4E8C' }}>
                                      Fab P {String(m.cognaraCascade.plannedFabStart).slice(5, 10)}→{String(m.cognaraCascade.plannedFabFinish || '').slice(5, 10)}
                                    </span>
                                  )}
                                  {m.cognaraCascade.actualFabStart && (
                                    <span className="px-0.5 rounded" style={{ background: '#DCFCE7', color: '#166534' }}>
                                      A {String(m.cognaraCascade.actualFabStart).slice(5, 10)}
                                    </span>
                                  )}
                                  {m.cognaraCascade.plannedErectStart && (
                                    <span className="px-0.5 rounded" style={{ background: '#F3E8FF', color: '#6B21A8' }}>
                                      Erc P {String(m.cognaraCascade.plannedErectStart).slice(5, 10)}→{String(m.cognaraCascade.plannedErectFinish || '').slice(5, 10)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                          : m.cognaraSuggestedActivityId
                            ? <span className="italic" style={{ color: '#F26B1F' }}>Cognara suggested</span>
                            : <span className="italic">–</span>}
                      </td>
                      <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{m.section}</td>
                      <td className="px-2 py-1 text-center" style={{ color: 'var(--text-primary)' }}>{m.pieceCount}</td>
                      <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtWt(m.totalWeight)}</td>
                      <td className="px-2 py-1">
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: allocPct >= 100 ? '#22c55e20' : allocPct > 0 ? '#f59e0b20' : '#ef444420', color: allocPct >= 100 ? '#22c55e' : allocPct > 0 ? '#f59e0b' : '#ef4444' }}>
                          {fmtPct(allocPct)}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        {m.fabricationBlocked ? <Lock className="w-3 h-3 inline" style={{ color: '#ef4444' }} /> : <span style={{ color: '#22c55e' }}>Ready</span>}
                      </td>
                      {FAB_STAGES.map(s => (
                        <td key={s} className="px-2 py-1 text-center">
                          {fab[s]?.isComplete ? <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: '#22c55e20', color: '#22c55e' }}>{String(fab[s]?.date || '').slice(0, 10) || 'Done'}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                        </td>
                      ))}
                      {EREC_STAGES.map(s => (
                        <td key={s} className="px-2 py-1 text-center">
                          {erec[s]?.isComplete ? <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: '#22c55e20', color: '#22c55e' }}>{String(erec[s]?.date || '').slice(0, 10) || 'Done'}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {marks.length === 0 && <tr><td colSpan={16} className="text-center py-3" style={{ color: 'var(--text-muted)' }}>No mark numbers found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {linkerMode && (
          <SWMMarkActivityLinker
            projectId={projectId} structuralTag={structuralTag}
            initialSelection={Array.from(selectedMarks)}
            mode={linkerMode}
            onClose={() => setLinkerMode(null)}
            onDone={() => { setSelectedMarks(new Set()); load(); }}
          />
        )}
      </td>
    </tr>
  );
}

export default function SWMWorkfrontStatus({ projectId, filters }) {
  const [tags, setTags] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedTag, setExpandedTag] = useState(null);
  const [groupBy, setGroupBy] = useState('area');
  const [viewMode, setViewMode] = useState('table');
  // Multi-select for bulk-delete
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Cognara planner state — either a single-tag drawer or a bulk-link modal.
  const [plannerTag, setPlannerTag] = useState(null);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  const [bulkLinkActivities, setBulkLinkActivities] = useState([]);
  const [bulkLinkQ, setBulkLinkQ] = useState('');
  const [bulkLinkPicked, setBulkLinkPicked] = useState(null);
  const [bulkLinkBusy, setBulkLinkBusy] = useState(false);
  const [showRollup, setShowRollup] = useState(false);
  // Cognara-based bulk tag→activity linker (with cascade to marks).
  const [tagCognaraLinkerOpen, setTagCognaraLinkerOpen] = useState(false);
  const [tagCognaraBusy, setTagCognaraBusy] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagLinkDecisions, setTagLinkDecisions] = useState({});
  const [tagSuggestRationale, setTagSuggestRationale] = useState('');
  // ── Cognara MP & Equipment HITL state ────────────────────────────────
  const [mpEqOpen, setMpEqOpen] = useState(false);
  const [mpEqLoading, setMpEqLoading] = useState(false);
  const [mpEqProposals, setMpEqProposals] = useState([]);
  const [mpEqNote, setMpEqNote] = useState(null);
  const [mpEqApproved, setMpEqApproved] = useState({});   // { `${tag}|${mark}`: bool }
  const [mpEqPushing, setMpEqPushing] = useState(false);
  const [mpEqDiff, setMpEqDiff] = useState(null);          // {totals:{...}, tradesToSeed:[], details:[]}
  const [mpEqRewriting, setMpEqRewriting] = useState(false);

  const rewriteRationale = async () => {
    if (mpEqProposals.length === 0) return;
    setMpEqRewriting(true);
    try {
      // Flatten to shape the rewrite endpoint expects.
      const marks = [];
      mpEqProposals.forEach(p => (p.marks || []).forEach(m => marks.push({
        structuralTag: p.structuralTag, markNumber: m.markNumber,
        manpower: m.manpower, equipment: m.equipment,
      })));
      const { data } = await api.post(
        `/v1/projects/${projectId}/swm/cognara/manpower-equipment/rewrite-rationale`,
        { marks },
      );
      // Merge rewritten rationales back by mark
      const byMark = {};
      (data.marks || []).forEach(m => { byMark[m.markNumber] = m; });
      setMpEqProposals(prev => prev.map(p => ({
        ...p,
        marks: p.marks.map(m => {
          const r = byMark[m.markNumber];
          if (!r) return m;
          return {
            ...m,
            manpower: m.manpower.map(mp => {
              const match = (r.manpower || []).find(x => x.trade === mp.trade && x.phase === mp.phase);
              return match ? { ...mp, rationale: match.rationale || mp.rationale } : mp;
            }),
            equipment: m.equipment.map(eq => {
              const match = (r.equipment || []).find(x => x.category === eq.category && x.phase === eq.phase);
              return match ? { ...eq, rationale: match.rationale || eq.rationale } : eq;
            }),
          };
        }),
      })));
      toast.success(data.note || 'Cognara refined the rationales');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cognara rewrite failed');
    } finally { setMpEqRewriting(false); }
  };

  const previewDiff = async () => {
    const flatPlans = [];
    mpEqProposals.forEach(p => (p.marks || []).forEach(m => {
      if (mpEqApproved[`${p.structuralTag}|${m.markNumber}`]) {
        flatPlans.push({ structuralTag: p.structuralTag, ...m });
      }
    }));
    if (flatPlans.length === 0) { toast.warning('Nothing approved to diff.'); return; }
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/swm/cognara/manpower-equipment/push-diff`,
        { plans: flatPlans },
      );
      setMpEqDiff(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Diff preview failed'); }
  };

  const pushMpEqPlans = async () => {
    const flatPlans = [];
    mpEqProposals.forEach(p => (p.marks || []).forEach(m => {
      if (mpEqApproved[`${p.structuralTag}|${m.markNumber}`]) {
        flatPlans.push({ structuralTag: p.structuralTag, ...m });
      }
    }));
    if (flatPlans.length === 0) { toast.warning('Nothing approved to push.'); return; }
    setMpEqPushing(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/swm/cognara/manpower-equipment/push`,
        { plans: flatPlans },
      );
      toast.success(`Pushed ${data.manpowerWritten} manpower + ${data.equipmentWritten} equipment allocations`);
      setMpEqOpen(false); setMpEqProposals([]); setMpEqApproved({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    } finally { setMpEqPushing(false); }
  };

  const toggleSelect = (tagId) => setSelectedTags(prev => {
    const n = new Set(prev);
    if (n.has(tagId)) n.delete(tagId); else n.add(tagId);
    return n;
  });
  const clearSelection = () => setSelectedTags(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.workfrontStatus) params.set('workfrontStatus', filters.workfrontStatus);
      const [tagsRes, sumRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/swm/tags?${params}`),
        api.get(`/v1/projects/${projectId}/swm/summary`),
      ]);
      setTags(tagsRes.data?.tags || []);
      setSummary(sumRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * Cascade-delete a structural tag + its mark numbers + tag/model mappings.
   * Optimistic UI removal followed by a full refetch so summary state stays in sync.
   */
  const handleDeleteTag = useCallback(async (structuralTag, e) => {
    e?.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete tag "${structuralTag}" and ALL its mark numbers?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/v1/projects/${projectId}/swm/tags`, { params: { structuralTag } });
      setTags(prev => prev.filter(t => t.structuralTag !== structuralTag));
      if (expandedTag === structuralTag) setExpandedTag(null);
      fetchData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Delete failed: ' + (err?.response?.data?.detail || err?.message || 'Unknown error'));
    }
  }, [projectId, expandedTag, fetchData]);

  /** Bulk cascade-delete every currently selected tag + its dependents. */
  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedTags);
    if (ids.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} tag${ids.length === 1 ? '' : 's'} and ALL their mark numbers?\n\nThis cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/swm/tags/bulk-delete`, { structuralTags: ids });
      setTags(prev => prev.filter(t => !selectedTags.has(t.structuralTag)));
      clearSelection();
      setExpandedTag(null);
      // eslint-disable-next-line no-alert
      window.alert(`Deleted ${r.data?.tagCount || ids.length} tags (${r.data?.totalDeleted || 0} total docs cascaded).`);
      fetchData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Bulk delete failed: ' + (err?.response?.data?.detail || err?.message || 'Unknown error'));
    } finally {
      setBulkDeleting(false);
    }
  }, [projectId, selectedTags, fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const dist = summary?.stateDistribution || {};
  const totalTags = tags.length;
  const filteredTags = tags.filter(t => {
    if (search && !t.structuralTag?.toLowerCase().includes(search.toLowerCase()) && !t.structureNameRef?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters?.area && t.area !== filters.area) return false;
    if (filters?.priority && t.priority !== Number(filters.priority)) return false;
    return true;
  });

  const groups = {};
  filteredTags.forEach(t => {
    const key = groupBy === 'area' ? (t.area || 'Unassigned') : groupBy === 'priority' ? `P${t.priority || '?'}` : (t.structureType || 'Other');
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });

  return (
    <div className="space-y-3" data-testid="swm-workfront-status">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-1.5" data-testid="swm-view-toggle">
        <button onClick={() => setViewMode('table')} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors"
          style={{ background: viewMode === 'table' ? '#F59206' : 'var(--card-bg)', color: viewMode === 'table' ? '#fff' : 'var(--text-muted)', border: viewMode === 'table' ? 'none' : '1px solid var(--border-color)' }}
          data-testid="swm-view-table">
          <LayoutList className="w-3.5 h-3.5" /> Table View
        </button>
        <button onClick={() => setViewMode('tree')} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors"
          style={{ background: viewMode === 'tree' ? '#F59206' : 'var(--card-bg)', color: viewMode === 'tree' ? '#fff' : 'var(--text-muted)', border: viewMode === 'tree' ? 'none' : '1px solid var(--border-color)' }}
          data-testid="swm-view-tree">
          <GitBranch className="w-3.5 h-3.5" /> Decomposition Tree
        </button>
        <button onClick={() => setShowRollup(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors"
          style={{ background: 'rgba(242,107,31,0.10)', color: '#F26B1F', border: '1px solid rgba(242,107,31,0.35)' }}
          data-testid="swm-open-rollup"
          title="Cognara Manpower + Equipment S-curve across every APPROVED tag">
          <BarChart3 className="w-3.5 h-3.5" /> Manpower Rollup
        </button>
      </div>

      {viewMode === 'tree' ? (
        <SWMDecompositionTree projectId={projectId} filters={filters} />
      ) : (
      <div className="space-y-3">
      {/* State Distribution Bar */}
      <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        {Object.entries(STATUS_COLORS).map(([key, cfg]) => {
          const count = dist[key.charAt(0).toLowerCase() + key.slice(1).replace(/_([A-Z])/g, (_, c) => c)] || dist[key.toLowerCase().replace(/_/g, '')] || 0;
          const pct = totalTags > 0 ? (count / totalTags * 100).toFixed(0) : 0;
          const camelKey = key === 'UNALLOCATED' ? 'unallocated' : key === 'PARTIAL_SOFT' ? 'partialSoft' : key === 'READY_SOFT' ? 'readySoft' : key === 'PO_READY' ? 'poReady' : key === 'PARTIAL_HARD' ? 'partialHard' : 'fullyAllocated';
          const val = dist[camelKey] || 0;
          return (
            <div key={key} className="flex items-center gap-1 text-[10px]" data-testid={`swm-dist-${key}`}>
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: cfg.text }} />
              <span style={{ color: 'var(--text-muted)' }}>{cfg.label}:</span>
              <span className="font-bold" style={{ color: cfg.text }}>{val}</span>
            </div>
          );
        })}
      </div>

      {/* Search & Group Controls + Bulk-delete toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 px-2 py-1 rounded-md border flex-1 max-w-xs" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)' }}>
          <Search className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tags..." className="bg-transparent border-none outline-none text-[11px] w-full" style={{ color: 'var(--text-primary)' }} data-testid="swm-workfront-search" />
        </div>
        <div className="flex gap-1">
          {['area', 'priority', 'type'].map(g => (
            <button key={g} onClick={() => setGroupBy(g)} className="px-2.5 py-1 text-[10px] rounded-md transition-colors" data-testid={`swm-group-${g}`}
              style={{ background: groupBy === g ? '#F59206' : 'var(--card-bg)', color: groupBy === g ? '#fff' : 'var(--text-muted)', border: groupBy === g ? 'none' : '1px solid var(--border-color)' }}>
              {g === 'area' ? 'By Area' : g === 'priority' ? 'By Priority' : 'By Type'}
            </button>
          ))}
        </div>
        {selectedTags.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-1 rounded-lg border ml-auto" style={{ borderColor: 'rgb(239,68,68,0.4)', background: 'rgb(239,68,68,0.08)' }} data-testid="swm-bulk-toolbar">
            <span className="text-[11px] font-mono" style={{ color: 'rgb(248,113,113)' }}>{selectedTags.size} selected</span>
            <button onClick={async () => {
                setBulkLinkOpen(true); setBulkLinkPicked(null); setBulkLinkQ('');
                try {
                  const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/macro-activities`);
                  setBulkLinkActivities(data.activities || []);
                  if (!data.activities?.length && data.note) toast.info(data.note);
                } catch { toast.error('Failed to load macro activities'); }
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium transition-colors hover:bg-blue-500/20"
              style={{ color: '#143A6B', border: '1px solid rgb(20,58,107,0.4)' }}
              data-testid="swm-bulk-link-btn">
              <Link2 className="w-3 h-3"/> Manual link
            </button>
            <button onClick={() => setTagCognaraLinkerOpen(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium text-white transition-colors"
              style={{ background: '#F26B1F' }}
              data-testid="swm-bulk-cognara-link-btn"
              title="Cognara analyses selected tags + SO-97.16, proposes activity per tag, then cascades dates to marks">
              <Sparkles className="w-3 h-3"/> Cognara: Schedule
            </button>
            <button onClick={async () => {
                setMpEqOpen(true); setMpEqLoading(true); setMpEqNote(null);
                try {
                  const { data } = await api.post(
                    `/v1/projects/${projectId}/swm/cognara/manpower-equipment/recommend`,
                    { structuralTags: Array.from(selectedTags), useCognara: false },
                  );
                  setMpEqProposals(data.proposals || []);
                  setMpEqNote(data.cognaraNote || null);
                  // Pre-approve everything so a happy-path user just clicks Push.
                  const acc = {};
                  (data.proposals || []).forEach(p => (p.marks || []).forEach(m => {
                    acc[`${p.structuralTag}|${m.markNumber}`] = true;
                  }));
                  setMpEqApproved(acc);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Cognara MP&Eq recommend failed');
                  setMpEqOpen(false);
                } finally { setMpEqLoading(false); }
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium text-white transition-colors"
              style={{ background: '#0EA5E9' }}
              data-testid="swm-bulk-cognara-mpeq-btn"
              title="Cognara analyses selected tags/marks and proposes manpower crews + equipment per mark with subjected dates. Approve rows and push to Manpower & Equipment modules.">
              <Sparkles className="w-3 h-3"/> Cognara: MP & Equipment
            </button>
            <button onClick={handleBulkDelete} disabled={bulkDeleting}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium transition-colors hover:bg-red-500/20 disabled:opacity-50"
              style={{ color: 'rgb(248,113,113)', border: '1px solid rgb(239,68,68,0.4)' }}
              data-testid="swm-bulk-delete-btn">
              <Trash2 className="w-3 h-3"/> {bulkDeleting ? 'Deleting…' : `Delete ${selectedTags.size} tag${selectedTags.size === 1 ? '' : 's'}`}
            </button>
            <button onClick={clearSelection} className="text-[10px] underline" style={{ color: 'var(--text-muted)' }} data-testid="swm-bulk-clear-btn">Clear</button>
          </div>
        )}
      </div>

      {/* Unified table with group-header rows separating each area/priority/type */}
      <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="w-6 px-1 text-center">
                  <input type="checkbox"
                    checked={filteredTags.length > 0 && filteredTags.every(t => selectedTags.has(t.structuralTag))}
                    ref={el => { if (el) el.indeterminate = filteredTags.some(t => selectedTags.has(t.structuralTag)) && !filteredTags.every(t => selectedTags.has(t.structuralTag)); }}
                    onChange={(e) => {
                      setSelectedTags(prev => {
                        const n = new Set(prev);
                        if (e.target.checked) filteredTags.forEach(t => n.add(t.structuralTag));
                        else filteredTags.forEach(t => n.delete(t.structuralTag));
                        return n;
                      });
                    }}
                    className="accent-red-500 cursor-pointer"
                    data-testid="swm-select-all-unified"
                    title="Select all filtered tags"/>
                </th>
                <th className="w-6 px-1" />
                {['Tag #', 'Structure', 'Area', 'Priority', 'Macro Activity', 'Section', 'MTO Wt (kg)', 'Soft %', 'Hard %', 'Fab %', 'Erec %', 'Status', 'Fab Loc', 'Marks', 'Cognara', ''].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, groupTags]) => (
                <Fragment key={groupName}>
                  <tr style={{ background: 'var(--card-bg-alt)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}
                    data-testid={`swm-group-header-${groupName}`}>
                    <td className="px-1 text-center">
                      <input type="checkbox"
                        checked={groupTags.length > 0 && groupTags.every(t => selectedTags.has(t.structuralTag))}
                        ref={el => { if (el) el.indeterminate = groupTags.some(t => selectedTags.has(t.structuralTag)) && !groupTags.every(t => selectedTags.has(t.structuralTag)); }}
                        onChange={(e) => {
                          setSelectedTags(prev => {
                            const n = new Set(prev);
                            if (e.target.checked) groupTags.forEach(t => n.add(t.structuralTag));
                            else groupTags.forEach(t => n.delete(t.structuralTag));
                            return n;
                          });
                        }}
                        className="accent-red-500 cursor-pointer"
                        data-testid={`swm-select-all-${groupName}`}
                        title={`Select all tags in ${groupName}`}/>
                    </td>
                    <td colSpan={16} className="px-3 py-2 text-[11px] font-bold" style={{ color: 'var(--text-primary)' }}>
                      <span className="inline-flex items-center gap-2">
                        <span>{groupName}</span>
                        <span className="text-[10px] font-normal px-2 py-0.5 rounded" style={{ background: '#F5920615', color: '#F59206' }}>{groupTags.length} tag{groupTags.length === 1 ? '' : 's'}</span>
                      </span>
                    </td>
                  </tr>
                  {groupTags.map(tag => {
                  const isExpanded = expandedTag === tag.structuralTag;
                  const wf = tag.workfrontStatus || 'UNALLOCATED';
                  const sc = STATUS_COLORS[wf] || STATUS_COLORS.UNALLOCATED;
                  const softPct = tag.softAllocation?.allocationPercent || 0;
                  const hardPct = tag.hardAllocation?.allocationPercent || 0;
                  const fabPct = tag.fabricationProgress?.overallFabProgressPct || 0;
                  const erecPct = tag.erectionProgress?.overallErectionProgressPct || 0;
                  const canExpand = wf !== 'UNALLOCATED';
                  return (
                    <Fragment key={tag.structuralTag}>
                      <tr className="hover:brightness-95 transition-colors cursor-pointer" style={{ borderBottom: '1px solid var(--border-color)', background: selectedTags.has(tag.structuralTag) ? 'rgb(239,68,68,0.08)' : undefined }}
                        onClick={() => canExpand && setExpandedTag(isExpanded ? null : tag.structuralTag)} data-testid={`swm-tag-row-${tag.structuralTag}`}>
                        <td className="px-1 text-center" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={selectedTags.has(tag.structuralTag)}
                            onChange={() => toggleSelect(tag.structuralTag)}
                            className="accent-red-500 cursor-pointer"
                            data-testid={`swm-select-${tag.structuralTag}`}/>
                        </td>
                        <td className="px-1 text-center">
                          {canExpand && (isExpanded ? <ChevronDown className="w-3 h-3" style={{ color: '#F59206' }} /> : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />)}
                        </td>
                        <td className="px-2 py-1.5 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{tag.structuralTag}</td>
                        <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{tag.structureNameRef}</td>
                        <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{tag.area}</td>
                        <td className="px-2 py-1.5">
                          {tag.priority ? <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][tag.priority]}20`, color: ['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][tag.priority] }}>P{tag.priority}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {tag.linkedMacroActivityName ? (
                            <div className="flex flex-col gap-0.5 max-w-[320px]">
                              <div className="inline-flex items-center gap-1"
                                title={`${(tag.linkedMacroActivityWbsPath || []).map(x => `${x.wbs} · ${x.name}`).join('  ›  ') || tag.linkedMacroActivityWBS || ''} · ${tag.linkedMacroActivityName}`}>
                                <Link2 className="w-2.5 h-2.5 shrink-0" style={{ color: '#143A6B' }} />
                                <span className="font-mono text-[8.5px] truncate" style={{ color: '#64748B' }}>
                                  {(tag.linkedMacroActivityWbsPath && tag.linkedMacroActivityWbsPath.length > 1)
                                    ? tag.linkedMacroActivityWbsPath.slice(0, -1).map(x => x.name).join(' › ') + ' › '
                                    : ''}
                                </span>
                                {tag.linkedMacroActivityWBS && (
                                  <span className="font-mono text-[9px] px-1 rounded shrink-0" style={{ background: 'rgba(20,58,107,0.08)', color: '#143A6B' }}>{tag.linkedMacroActivityWBS}</span>
                                )}
                                <span className="truncate font-medium" style={{ color: '#143A6B' }}>{tag.linkedMacroActivityName}</span>
                              </div>
                              {tag.linkedMacroActivityDates && (
                                <div className="flex items-center gap-1.5 text-[8.5px] pl-3.5 flex-wrap">
                                  {tag.linkedMacroActivityDates.plannedStart && (
                                    <span className="px-1 rounded" style={{ background: '#DBEAFE', color: '#1E4E8C' }} title="Planned">
                                      P {String(tag.linkedMacroActivityDates.plannedStart).slice(0, 10)} → {String(tag.linkedMacroActivityDates.plannedFinish || '').slice(0, 10)}
                                    </span>
                                  )}
                                  {tag.linkedMacroActivityDates.forecastStart && (
                                    <span className="px-1 rounded" style={{ background: '#FEF3C7', color: '#92400E' }} title="Forecast">
                                      F {String(tag.linkedMacroActivityDates.forecastStart).slice(0, 10)} → {String(tag.linkedMacroActivityDates.forecastFinish || '').slice(0, 10)}
                                    </span>
                                  )}
                                  {tag.linkedMacroActivityDates.actualStart && (
                                    <span className="px-1 rounded" style={{ background: '#DCFCE7', color: '#166534' }} title="Actual">
                                      A {String(tag.linkedMacroActivityDates.actualStart).slice(0, 10)} → {String(tag.linkedMacroActivityDates.actualFinish || '').slice(0, 10) || '…'}
                                    </span>
                                  )}
                                  {typeof tag.linkedMacroActivityDates.slippageDays === 'number' && tag.linkedMacroActivityDates.slippageDays > 0 && (
                                    <span className="px-1.5 rounded flex items-center gap-1 animate-pulse font-bold"
                                      style={{ background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5' }}
                                      title={`Slipped by ${tag.linkedMacroActivityDates.slippageDays} day(s) — forecast/actual finish exceeds planned finish`}
                                      data-testid={`schedule-delta-${tag.structuralTag}`}>
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#B91C1C' }} />
                                      Δ +{tag.linkedMacroActivityDates.slippageDays}d
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : <span className="italic">Not linked</span>}
                        </td>
                        <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{tag.section}</td>
                        <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtWt(tag.totalMTOWeight)}</td>
                        <td className="px-2 py-1.5">
                          {hardPct >= 99.99
                            ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#3b82f620', color: '#3b82f6' }} title="Fully hard-allocated — no residual soft demand">N/A · Hard 100%</span>
                            : <PercentBar value={softPct} color="#3b82f6" />}
                        </td>
                        <td className="px-2 py-1.5"><PercentBar value={hardPct} color="#22c55e" /></td>
                        <td className="px-2 py-1.5"><PercentBar value={fabPct} color="#143A6B" /></td>
                        <td className="px-2 py-1.5"><PercentBar value={erecPct} color="#f59e0b" /></td>
                        <td className="px-2 py-1.5">
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>
                        </td>
                        <td className="px-2 py-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{tag.fabricationLocation}</td>
                        <td className="px-2 py-1.5 text-center">
                          {canExpand && <Package className="w-3 h-3 inline" style={{ color: '#F59206' }} />}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={(e) => { e.stopPropagation(); setPlannerTag(tag.structuralTag); }}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors text-[9px] font-mono uppercase"
                            style={{
                              background: tag.cognaraStatus === 'APPROVED' ? 'rgba(14,124,107,0.15)'
                                : tag.cognaraStatus === 'SUGGESTED' ? 'rgba(242,107,31,0.15)'
                                : tag.cognaraStatus === 'PARTIALLY_APPROVED' ? 'rgba(245,146,6,0.15)'
                                : 'transparent',
                              color: tag.cognaraStatus === 'APPROVED' ? '#0E7C6B'
                                : tag.cognaraStatus === 'SUGGESTED' ? '#F26B1F'
                                : tag.cognaraStatus === 'PARTIALLY_APPROVED' ? '#F59206'
                                : 'var(--text-muted)',
                              border: tag.cognaraStatus ? 'none' : '1px dashed var(--border-color)',
                            }}
                            title={tag.cognaraStatus ? `Cognara plan · ${tag.cognaraStatus}` : 'Open Cognara planner'}
                            data-testid={`swm-cognara-open-${tag.structuralTag}`}>
                            <Sparkles className="w-2.5 h-2.5" />
                            {tag.cognaraStatus || 'Plan'}
                          </button>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={(e) => handleDeleteTag(tag.structuralTag, e)}
                            className="inline-flex items-center justify-center p-1 rounded transition-colors hover:bg-red-500/15 hover:text-red-400"
                            style={{ color: 'var(--text-muted)' }}
                            title={`Delete tag ${tag.structuralTag} and all its marks`}
                            data-testid={`delete-tag-${tag.structuralTag}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && <MarkDetailPanel projectId={projectId} structuralTag={tag.structuralTag} />}
                    </Fragment>
                  );
                })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredTags.length === 0 && <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No tags found matching filters</div>}
      </div>
      )}
      {plannerTag && <SWMCognaraPlanner projectId={projectId} structuralTag={plannerTag} onClose={() => setPlannerTag(null)} onSaved={fetchData} />}
      {mpEqOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-mpeq-modal">
          <div className="w-full max-w-5xl rounded-xl overflow-hidden shadow-2xl flex flex-col" style={{ background: 'var(--card-bg)', maxHeight: '88vh' }}>
            <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
              <Sparkles className="w-4 h-4" style={{ color: '#0EA5E9' }} />
              <div className="flex-1">
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Cognara — Manpower & Equipment plan</div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Approve the mark-level crew + equipment plans below, then push to Manpower & Equipment modules.</div>
              </div>
              <button onClick={() => setMpEqOpen(false)} className="text-[11px] px-2 py-1 rounded" style={{ color: 'var(--text-muted)' }} data-testid="swm-mpeq-close">Cancel</button>
            </div>
            {mpEqNote && (
              <div className="mx-4 mt-3 rounded p-2 text-[11px]" style={{ background: '#0EA5E912', border: '1px solid #0EA5E940' }}>
                <b style={{ color: '#0EA5E9' }}>Cognara notes:</b> {mpEqNote}
              </div>
            )}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {mpEqLoading ? (
                <div className="p-10 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
                  Cognara is sizing crews and equipment for {selectedTags.size} tag{selectedTags.size === 1 ? '' : 's'}…
                </div>
              ) : mpEqProposals.length === 0 ? (
                <div className="p-10 text-center text-xs" style={{ color: 'var(--text-disabled)' }}>
                  No marks under the selected tags — link a schedule and seed marks first.
                </div>
              ) : mpEqProposals.map(p => {
                const allChecked = (p.marks || []).every(m => mpEqApproved[`${p.structuralTag}|${m.markNumber}`]);
                return (
                  <div key={p.structuralTag} className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-mpeq-tag-${p.structuralTag}`}>
                    <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'var(--card-bg-alt)' }}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={allChecked}
                          onChange={(e) => setMpEqApproved(prev => {
                            const n = { ...prev };
                            (p.marks || []).forEach(m => n[`${p.structuralTag}|${m.markNumber}`] = e.target.checked);
                            return n;
                          })}
                          className="accent-sky-600" />
                        <span className="font-mono text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#0EA5E922', color: '#0EA5E9' }}>{p.structuralTag}</span>
                      </label>
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {p.marks?.length || 0} marks · {p.totalTagWeightKg.toLocaleString()} kg{p.linkedMacroActivityName ? ` · ${p.linkedMacroActivityName}` : ''}
                      </span>
                    </div>
                    <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      {(p.marks || []).map(m => {
                        const key = `${p.structuralTag}|${m.markNumber}`;
                        const checked = !!mpEqApproved[key];
                        return (
                          <div key={m.markNumber} className="p-2 flex items-start gap-2 text-[10px]"
                            style={{ background: checked ? 'rgba(14,165,233,0.05)' : 'transparent' }}
                            data-testid={`swm-mpeq-mark-${m.markNumber}`}>
                            <input type="checkbox" checked={checked}
                              onChange={() => setMpEqApproved(prev => ({ ...prev, [key]: !prev[key] }))}
                              className="accent-sky-600 mt-1" />
                            <div className="w-32 shrink-0">
                              <div className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{m.markNumber}</div>
                              <div style={{ color: 'var(--text-muted)' }}>{m.totalWeightKg.toLocaleString()} kg</div>
                            </div>
                            <div className="w-40 shrink-0 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                              <div>
                                <span className="uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Plan</span>{' '}
                                {m.plannedFabStart ? new Date(m.plannedFabStart).toLocaleDateString() : '—'} → {m.plannedFabFinish ? new Date(m.plannedFabFinish).toLocaleDateString() : '—'}
                              </div>
                              <div style={{ color: '#0EA5E9' }}>
                                <span className="uppercase font-semibold">Fcst</span>{' '}
                                {m.forecastFabStart ? new Date(m.forecastFabStart).toLocaleDateString() : (m.fabStart ? new Date(m.fabStart).toLocaleDateString() : '—')} → {m.forecastFabFinish ? new Date(m.forecastFabFinish).toLocaleDateString() : (m.fabFinish ? new Date(m.fabFinish).toLocaleDateString() : '—')}
                              </div>
                              <div style={{ color: '#22C55E' }}>
                                <span className="uppercase font-semibold">Actl</span>{' '}
                                {m.actualFabStart ? new Date(m.actualFabStart).toLocaleDateString() : '—'} → {m.actualFabFinish ? new Date(m.actualFabFinish).toLocaleDateString() : '—'}
                              </div>
                              <div className="mt-1 pt-1 border-t" style={{ borderColor: 'var(--border-color)' }}>
                                <div><span className="uppercase font-semibold">E-Plan</span> {m.plannedErectionStart ? new Date(m.plannedErectionStart).toLocaleDateString() : '—'} → {m.plannedErectionFinish ? new Date(m.plannedErectionFinish).toLocaleDateString() : '—'}</div>
                                <div style={{ color: '#0EA5E9' }}><span className="uppercase font-semibold">E-Fcst</span> {m.forecastErectionStart ? new Date(m.forecastErectionStart).toLocaleDateString() : (m.erectionStart ? new Date(m.erectionStart).toLocaleDateString() : '—')} → {m.forecastErectionFinish ? new Date(m.forecastErectionFinish).toLocaleDateString() : (m.erectionFinish ? new Date(m.erectionFinish).toLocaleDateString() : '—')}</div>
                                <div style={{ color: '#22C55E' }}><span className="uppercase font-semibold">E-Actl</span> {m.actualErectionStart ? new Date(m.actualErectionStart).toLocaleDateString() : '—'} → {m.actualErectionFinish ? new Date(m.actualErectionFinish).toLocaleDateString() : '—'}</div>
                              </div>
                            </div>
                            <div className="flex-1 flex flex-wrap gap-1">
                              {(m.manpower || []).map((mp, i) => (
                                <label key={`mp-${i}`}
                                  className="px-1.5 py-0.5 rounded flex items-center gap-1 cursor-help"
                                  style={{ background: mp.phase === 'FAB' ? '#22C55E20' : '#F5920620', color: mp.phase === 'FAB' ? '#16A34A' : '#F59206' }}
                                  title={mp.rationale || `${mp.headcount} ${mp.trade} (${mp.phase})`}>
                                  <input
                                    type="number" min="0"
                                    value={mp.headcount}
                                    onChange={(e) => {
                                      const val = Math.max(0, parseInt(e.target.value) || 0);
                                      setMpEqProposals(prev => prev.map(pp => pp.structuralTag !== p.structuralTag ? pp : {
                                        ...pp,
                                        marks: pp.marks.map(mm => mm.markNumber !== m.markNumber ? mm : {
                                          ...mm,
                                          manpower: mm.manpower.map((mpx, xi) => xi !== i ? mpx : { ...mpx, headcount: val }),
                                        }),
                                      }));
                                    }}
                                    className="w-8 text-center bg-transparent border-b border-current outline-none text-[10px]"
                                    style={{ color: 'inherit' }}
                                    data-testid={`swm-mpeq-hc-${m.markNumber}-${mp.trade}`}
                                  />
                                  {mp.trade}
                                </label>
                              ))}
                              {(m.equipment || []).map((eq, i) => (
                                <label key={`eq-${i}`}
                                  className="px-1.5 py-0.5 rounded flex items-center gap-1 cursor-help"
                                  style={{ background: '#6366F120', color: '#6366F1' }}
                                  title={eq.rationale || `${eq.qty} ${eq.category}`}>
                                  <input
                                    type="number" min="0"
                                    value={eq.qty}
                                    onChange={(e) => {
                                      const val = Math.max(0, parseInt(e.target.value) || 0);
                                      setMpEqProposals(prev => prev.map(pp => pp.structuralTag !== p.structuralTag ? pp : {
                                        ...pp,
                                        marks: pp.marks.map(mm => mm.markNumber !== m.markNumber ? mm : {
                                          ...mm,
                                          equipment: mm.equipment.map((eqx, xi) => xi !== i ? eqx : { ...eqx, qty: val }),
                                        }),
                                      }));
                                    }}
                                    className="w-8 text-center bg-transparent border-b border-current outline-none text-[10px]"
                                    style={{ color: 'inherit' }}
                                    data-testid={`swm-mpeq-qty-${m.markNumber}-${eq.category}`}
                                  />
                                  {eq.category}{eq.capacityTon ? ` (${eq.capacityTon} t)` : ''}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: 'var(--border-color)' }}>
              <div className="text-[11px] flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-muted)' }}>
                <span>{Object.values(mpEqApproved).filter(Boolean).length} mark plan{Object.values(mpEqApproved).filter(Boolean).length === 1 ? '' : 's'} approved</span>
                {mpEqDiff && (
                  <span className="flex items-center gap-2 px-2 py-1 rounded" style={{ background: 'rgba(14,165,233,0.08)' }}>
                    <span style={{ color: '#22C55E' }}>+{mpEqDiff.totals.manpowerInsert} MP new</span>
                    <span style={{ color: '#F59E0B' }}>~{mpEqDiff.totals.manpowerUpdate} MP upd</span>
                    <span style={{ color: '#6366F1' }}>+{mpEqDiff.totals.equipmentInsert} Eq new</span>
                    <span style={{ color: '#F59E0B' }}>~{mpEqDiff.totals.equipmentUpdate} Eq upd</span>
                    {mpEqDiff.tradesToSeed?.length > 0 && (
                      <span style={{ color: '#A855F7' }} title={mpEqDiff.tradesToSeed.join(', ')}>+{mpEqDiff.tradesToSeed.length} trade(s) auto-seed</span>
                    )}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={rewriteRationale} disabled={mpEqRewriting || mpEqLoading}
                  className="text-[11px] px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-60"
                  style={{ background: '#A855F718', color: '#A855F7', border: '1px solid #A855F740' }}
                  data-testid="swm-mpeq-rewrite-cognara">
                  {mpEqRewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Ask Cognara to refine rationale
                </button>
                <button onClick={previewDiff}
                  className="text-[11px] px-3 py-1.5 rounded flex items-center gap-1"
                  style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  data-testid="swm-mpeq-diff-preview">
                  Preview diff
                </button>
                <button onClick={() => setMpEqOpen(false)} className="text-[11px] px-3 py-1.5 rounded"
                  style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>Cancel</button>
                <button onClick={pushMpEqPlans} disabled={mpEqPushing}
                  className="text-[11px] px-3 py-1.5 rounded text-white font-semibold flex items-center gap-1 disabled:opacity-60"
                  style={{ background: '#0EA5E9' }}
                  data-testid="swm-mpeq-push">
                  {mpEqPushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Push to Manpower & Equipment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRollup && <SWMManpowerRollup projectId={projectId} onClose={() => setShowRollup(false)} />}
      {tagCognaraLinkerOpen && (
        <TagCognaraLinker
          projectId={projectId}
          selectedTags={Array.from(selectedTags)}
          activities={bulkLinkActivities}
          onClose={() => { setTagCognaraLinkerOpen(false); setTagSuggestions([]); setTagLinkDecisions({}); setTagSuggestRationale(''); }}
          onDone={() => { setTagCognaraLinkerOpen(false); setSelectedTags(new Set()); setTagSuggestions([]); setTagLinkDecisions({}); setTagSuggestRationale(''); fetchData(); }}
          suggestions={tagSuggestions} setSuggestions={setTagSuggestions}
          decisions={tagLinkDecisions} setDecisions={setTagLinkDecisions}
          rationale={tagSuggestRationale} setRationale={setTagSuggestRationale}
          busy={tagCognaraBusy} setBusy={setTagCognaraBusy}
        />
      )}
      {bulkLinkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-bulk-link-modal">
          <div className="w-full max-w-2xl rounded-xl overflow-hidden shadow-2xl" style={{ background: 'var(--card-bg)' }}>
            <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Link {selectedTags.size} tag{selectedTags.size === 1 ? '' : 's'} to a macro activity</div>
              <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Priority + planned dates flow down from the activity onto every selected tag and its marks.</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input value={bulkLinkQ} onChange={e => setBulkLinkQ(e.target.value)} placeholder="Search WBS / activity name…"
                  className="w-full pl-8 pr-2 py-1.5 rounded border text-[11px]"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="swm-bulk-link-search"/>
              </div>
              <div className="rounded border overflow-hidden max-h-[45vh] overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
                {bulkLinkActivities.length === 0 && (
                  <div className="p-4 text-center text-[11px] italic" style={{ color: 'var(--text-muted)' }}>
                    No macro schedule linked yet — set it up in Material Tracking → Linked Resources.
                  </div>
                )}
                {bulkLinkActivities
                  .filter(a => !bulkLinkQ.trim() || (a.wbs || '').toLowerCase().includes(bulkLinkQ.toLowerCase()) || (a.name || '').toLowerCase().includes(bulkLinkQ.toLowerCase()))
                  .slice(0, 500)
                  .map(a => {
                    const active = bulkLinkPicked?.id === a.id;
                    const canPick = !a.isSummary && !a.isMilestone;
                    return (
                      <button key={a.id} onClick={() => canPick && setBulkLinkPicked(a)} disabled={!canPick}
                        className="w-full text-left px-3 py-1.5 border-b flex items-center gap-2 hover:bg-slate-50/60 disabled:opacity-100 disabled:cursor-default"
                        style={{ borderColor: 'var(--border-color)', background: active ? 'rgba(242,107,31,0.14)' : (a.isSummary ? 'rgba(148,163,184,0.05)' : 'transparent'),
                                 paddingLeft: `${12 + (a.level || 0) * 18}px` }}
                        data-testid={`swm-bulk-link-activity-${a.id}`}>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                          style={{ background: a.isSummary ? 'rgba(20,58,107,0.16)' : 'rgba(20,58,107,0.08)',
                                   color: '#143A6B',
                                   fontWeight: a.isSummary ? 700 : 500 }}>{a.wbs}</span>
                        <span className={`text-[11.5px] flex-1 truncate ${a.isSummary ? 'font-semibold uppercase tracking-wide' : ''}`}
                          style={{ color: a.isSummary ? 'var(--text-primary)' : 'var(--text-primary)' }}
                          title={a.name}>{a.name}</span>
                        {a.isSummary && (
                          <span className="text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Summary</span>
                        )}
                        <span className="text-[10px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>
                          {a.plannedStart ? a.plannedStart.slice(0, 10) : '—'} → {a.plannedFinish ? a.plannedFinish.slice(0, 10) : '—'}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
            <div className="p-3 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
              <button onClick={() => setBulkLinkOpen(false)} className="px-3 py-1 rounded text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid="swm-bulk-link-cancel">Cancel</button>
              <button onClick={async () => {
                  if (!bulkLinkPicked) { toast.error('Pick an activity'); return; }
                  setBulkLinkBusy(true);
                  try {
                    const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/link-activity`, {
                      structuralTags: Array.from(selectedTags),
                      macroActivityId: bulkLinkPicked.id,
                    });
                    toast.success(`Linked ${data.modified} tag${data.modified === 1 ? '' : 's'} · P${data.priority} · ${data.linkedActivity.name}`);
                    setBulkLinkOpen(false); setSelectedTags(new Set()); fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
                  finally { setBulkLinkBusy(false); }
                }}
                disabled={bulkLinkBusy || !bulkLinkPicked}
                className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium text-white disabled:opacity-50"
                style={{ background: '#F26B1F' }}
                data-testid="swm-bulk-link-confirm">
                {bulkLinkBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Link2 className="w-3 h-3"/>} Link {selectedTags.size} tag{selectedTags.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PercentBar({ value, color }) {
  const v = Math.min(100, Math.max(0, value || 0));
  return (
    <div className="flex items-center gap-1">
      <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: `${color}15` }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="text-[9px] font-mono" style={{ color }}>{v.toFixed(0)}%</span>
    </div>
  );
}

/**
 * CascadeTimelineStrip — mini Gantt showing the mark's fab (blue) & erect
 * (orange) windows against the tag's macro-activity envelope (grey). Bars
 * outside the envelope render clamped and get a red edge marker so planners
 * see slippage at a glance.
 */
function CascadeTimelineStrip({ macroStart, macroFinish, fabStart, fabFinish, erectStart, erectFinish, rejected }) {
  const parse = (v) => { if (!v) return null; const t = Date.parse(String(v).slice(0, 10)); return Number.isFinite(t) ? t : null; };
  const ms = parse(macroStart), mf = parse(macroFinish);
  if (!ms || !mf || mf <= ms) {
    return <div className="text-[9px] italic" style={{ color: 'var(--text-muted)' }}>no window</div>;
  }
  const total = mf - ms;
  const segment = (start, finish) => {
    const s = parse(start), f = parse(finish);
    if (!s || !f || f <= s) return null;
    const leftPct   = Math.max(0, Math.min(100, ((s - ms) / total) * 100));
    const rightPct  = Math.max(0, Math.min(100, ((f - ms) / total) * 100));
    const widthPct  = Math.max(1, rightPct - leftPct);
    const overflowStart  = s < ms;
    const overflowFinish = f > mf;
    return { leftPct, widthPct, overflowStart, overflowFinish };
  };
  const fab   = segment(fabStart, fabFinish);
  const erect = segment(erectStart, erectFinish);
  const opacity = rejected ? 0.35 : 1;
  return (
    <div className="relative" title={`Macro ${String(macroStart).slice(0,10)} → ${String(macroFinish).slice(0,10)}`} style={{ opacity }}>
      <div className="relative h-4 rounded" style={{ background: 'rgba(148,163,184,0.18)', border: '1px solid rgba(148,163,184,0.35)' }}>
        {fab && (
          <div className="absolute top-0.5 h-1.5 rounded-sm" style={{
              left: `${fab.leftPct}%`, width: `${fab.widthPct}%`,
              background: '#143A6B',
              boxShadow: fab.overflowStart || fab.overflowFinish ? 'inset 0 0 0 1px #EF4444' : undefined,
            }} title={`Fab ${String(fabStart).slice(0,10)} → ${String(fabFinish).slice(0,10)}`} />
        )}
        {erect && (
          <div className="absolute bottom-0.5 h-1.5 rounded-sm" style={{
              left: `${erect.leftPct}%`, width: `${erect.widthPct}%`,
              background: '#F26B1F',
              boxShadow: erect.overflowStart || erect.overflowFinish ? 'inset 0 0 0 1px #EF4444' : undefined,
            }} title={`Erect ${String(erectStart).slice(0,10)} → ${String(erectFinish).slice(0,10)}`} />
        )}
      </div>
      <div className="flex items-center justify-between mt-0.5 text-[8.5px] font-mono" style={{ color: 'var(--text-muted)' }}>
        <span>{String(macroStart).slice(5, 10)}</span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-1 rounded-sm" style={{ background: '#143A6B' }} />fab
          <span className="inline-block w-2 h-1 rounded-sm ml-1" style={{ background: '#F26B1F' }} />erect
        </span>
        <span>{String(macroFinish).slice(5, 10)}</span>
      </div>
    </div>
  );
}

/**
 * TagCognaraLinker — bulk Cognara-suggested tag→activity linker.
 * Auto-runs Cognara on mount, shows per-tag suggestions with editable activity
 * dropdown + Approve/Reject, then submits with `cascadeSequence=true` so mark
 * dates are auto-generated via Phase-2 3D-study heuristics for every approved tag.
 */
function TagCognaraLinker({ projectId, selectedTags, activities: activitiesProp,
                            onClose, onDone, suggestions, setSuggestions,
                            decisions, setDecisions, rationale, setRationale,
                            busy, setBusy }) {
  const [activities, setActivities] = useState(activitiesProp || []);
  const [submitBusy, setSubmitBusy] = useState(false);
  // Two-phase state — 'link' (approve tag→activity) then 'preview' (approve
  // mark-level fab/erect dates BEFORE they are committed to plannedX fields).
  const [phase, setPhase] = useState('link');
  const [previewTags, setPreviewTags] = useState([]);      // [{structuralTag, tagDoc, marks, rationale}]
  const [markDecisions, setMarkDecisions] = useState({});  // { "tag::mno": {decision:'APPROVE'|'REJECT', edits?:{plannedFabStart,...}} }
  const [commitBusy, setCommitBusy] = useState(false);
  // Live analysis progress feed (SSE from /cognara-tag-links-stream)
  const [progressPhase, setProgressPhase] = useState('idle');  // 'idle' | 'loading_activities' | 'activities_loaded' | 'loading_tags' | 'tag_summarised' | 'analyzing' | 'streaming' | 'done' | 'error'
  const [progressMsg, setProgressMsg] = useState('');
  const [progressTagsProcessed, setProgressTagsProcessed] = useState(0);
  const [progressActivityCount, setProgressActivityCount] = useState(0);
  const [progressChars, setProgressChars] = useState(0);
  const [progressStartedAt, setProgressStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [cascadeLog, setCascadeLog] = useState([]);   // last-N SSE events for the cascade progress panel
  // Cascade job tracking — used for the % progress bar + cancel button.
  const [cascadeJobId, setCascadeJobId] = useState(null);
  const [cascadeProcessed, setCascadeProcessed] = useState(0);
  const [cascadeTotal, setCascadeTotal] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  // Track cascade failures per tag so we can offer a retry-from-here button.
  const [cascadeFailures, setCascadeFailures] = useState([]);   // [{structuralTag, error}]
  const [saveMappingBusy, setSaveMappingBusy] = useState(false);
  // Stable key for tags — prevents the SSE effect from re-firing on every parent
  // re-render (Array.from() creates a new identity each time which used to abort
  // & reseed decisions, making ticks/crosses appear "dead").
  const selectedTagsKey = selectedTags.join('|');
  const hasStreamedRef = useRef(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!progressStartedAt || progressPhase === 'done' || progressPhase === 'error' || progressPhase === 'idle') return;
    const t = setInterval(() => setElapsedSec(Math.floor((Date.now() - progressStartedAt) / 1000)), 250);
    return () => clearInterval(t);
  }, [progressStartedAt, progressPhase]);

  useEffect(() => {
    (async () => {
      if (activities.length === 0) {
        try {
          const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/macro-activities`);
          setActivities(data.activities || []);
        } catch { toast.error('Failed to load macro activities'); }
      }
    })();
  }, [projectId, activities.length]);

  useEffect(() => {
    // Guard: run the initial Cognara analysis exactly once per selection.
    // Uses the background-job + polling pattern (start → poll every 2s → done)
    // to sidestep both Cloudflare's 100s HTTP timeout AND its unpredictable
    // buffering of SSE responses in browser environments.
    //
    // React StrictMode double-mounts effects in dev. We MUST NOT set a
    // `cancelled` boolean on the first cleanup, otherwise the second mount's
    // async work silently no-ops. Instead we use a strictly-monotonic runId
    // ref: the async callback is only allowed to write state while its runId
    // still matches the ref.
    if (hasStreamedRef.current) return;
    if (suggestions.length > 0) return;
    if (!selectedTagsKey) return;
    hasStreamedRef.current = true;
    const myRunId = ++runIdRef.current;
    const isStale = () => runIdRef.current !== myRunId;
    let pollHandle = null;
    (async () => {
      setBusy(true);
      setProgressStartedAt(Date.now());
      setProgressPhase('queued');
      setProgressMsg('Queueing Cognara job…');
      let jobId = null;
      try {
        const { data: startResp } = await api.post(
          `/v1/projects/${projectId}/swm/cognara/tags/cognara-tag-links/start`,
          { structuralTags: selectedTagsKey.split('|') },
        );
        jobId = startResp.jobId;
        if (!jobId) throw new Error('Backend did not return a jobId');
      } catch (e) {
        if (isStale()) return;
        setProgressPhase('error');
        setBusy(false);
        toast.error(e.response?.data?.detail || e.message || 'Failed to start Cognara job');
        return;
      }

      const poll = async () => {
        if (isStale()) return;
        try {
          const { data: job } = await api.get(
            `/v1/projects/${projectId}/swm/cognara/tags/cognara-tag-links/status/${jobId}`,
          );
          if (isStale()) return;
          setProgressPhase(job.phase || job.status);
          if (job.message) setProgressMsg(job.message);
          setProgressActivityCount(job.activityCount || 0);
          setProgressTagsProcessed(job.tagsProcessed || 0);
          const list = job.suggestions || [];
          if (list.length) {
            setSuggestions(prev => {
              if (prev.length === list.length) return prev;
              const seen = new Set(prev.map(p => p.structuralTag));
              const additions = list.filter(s => !seen.has(s.structuralTag));
              if (!additions.length) return prev;
              setDecisions(pd => {
                const nd = { ...pd };
                additions.forEach(s => {
                  if (!nd[s.structuralTag]) nd[s.structuralTag] = { decision: 'APPROVE', activityId: s.activityId };
                });
                return nd;
              });
              return [...prev, ...additions];
            });
          }
          if (job.status === 'done') {
            setRationale(job.rationale || '');
            setProgressPhase('done');
            setBusy(false);
            toast.success(`Cognara proposed activities for ${job.total || list.length} tags`);
            return;
          }
          if (job.status === 'error') {
            setProgressPhase('error');
            setBusy(false);
            toast.error(job.error || 'Cognara analysis failed');
            return;
          }
          pollHandle = setTimeout(poll, 2000);
        } catch (e) {
          if (isStale()) return;
          setProgressPhase('error');
          setBusy(false);
          toast.error(e.response?.data?.detail || e.message || 'Cognara poll failed');
        }
      };
      poll();
    })();

    // Cleanup only clears the pending poll timer. It intentionally does NOT
    // invalidate the current runId — that's reserved for a genuine change of
    // `selectedTagsKey` (or unmount followed by a totally new mount, at which
    // point the ref is reset via hasStreamedRef).
    return () => { if (pollHandle) clearTimeout(pollHandle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedTagsKey]);

  /**
   * Save-only path — write the tag→activity mapping without triggering the
   * Cognara mark cascade. Uses the existing non-streaming `approve-tag-links`
   * with `cascadeSequence=false`. Users typically hit this when they want to
   * lock in the tag decisions but re-run the mark cascade later.
   */
  const saveMappingOnly = async () => {
    const rows = Object.entries(decisions).map(([tag, d]) => ({
      structuralTag: tag, decision: d.decision, activityId: d.activityId,
    })).filter(r => r.decision === 'APPROVE' ? !!r.activityId : true);
    if (!rows.length) { toast.error('No decisions to save'); return; }
    setSaveMappingBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/tags/approve-tag-links`,
        { decisions: rows, cascadeSequence: false });
      toast.success(`Mapping saved · ${data.approved} approved · ${data.rejected} rejected (no cascade)`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaveMappingBusy(false); }
  };

  /**
   * Retry cascade from a specific tag — re-submits only the tags after (and
   * including) `fromTag`. Used to resume from a failure without re-running
   * the whole batch.
   */
  const retryCascadeFrom = async (fromTag) => {
    const allRows = Object.entries(decisions).map(([tag, d]) => ({
      structuralTag: tag, decision: d.decision, activityId: d.activityId,
    })).filter(r => r.decision === 'APPROVE' ? !!r.activityId : true);
    const idx = allRows.findIndex(r => r.structuralTag === fromTag);
    if (idx < 0) { toast.error(`${fromTag} not found in decisions`); return; }
    const remaining = allRows.slice(idx);
    setCascadeFailures([]);
    await runCascadeJob(remaining, /* resumed */ true);
  };

  /**
   * Step A — Approve tag links via background-job + polling.
   *
   * SSE was unreliable here because Cognara can spend >60s on a single tag's
   * mark sequence and Cloudflare kills idle SSE connections at 60s. Polling
   * is immune — each `/status/{jobId}` request is a fresh short HTTP call.
   */
  const submit = () => {
    const rows = Object.entries(decisions).map(([tag, d]) => ({
      structuralTag: tag, decision: d.decision, activityId: d.activityId,
    })).filter(r => r.decision === 'APPROVE' ? !!r.activityId : true);
    if (!rows.length) { toast.error('No decisions to submit'); return; }
    setCascadeFailures([]);
    return runCascadeJob(rows, false);
  };

  /** Cooperative cancel — asks the backend worker to stop after the current tag. */
  const cancelCascade = async () => {
    if (!cascadeJobId || cancelling) return;
    setCancelling(true);
    try {
      await api.post(`/v1/projects/${projectId}/swm/cognara/tags/approve-tag-links/cancel/${cascadeJobId}`);
      toast.info('Stop requested — worker will exit after current tag.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
      setCancelling(false);
    }
  };

  const runCascadeJob = async (rows, resumed) => {
    setSubmitBusy(true);
    setCancelling(false);
    setCascadeJobId(null);
    setCascadeProcessed(0);
    setCascadeTotal(rows.length);
    setProgressStartedAt(Date.now());
    setProgressPhase('queued');
    setProgressMsg(`${resumed ? 'Resuming' : 'Cascading'} ${rows.length} tag${rows.length === 1 ? '' : 's'}…`);
    if (!resumed) setCascadeLog([]);
    let jobId = null;
    try {
      const { data: startResp } = await api.post(
        `/v1/projects/${projectId}/swm/cognara/tags/approve-tag-links/start`,
        { decisions: rows, cascadeSequence: true },
      );
      jobId = startResp.jobId;
      if (!jobId) throw new Error('Backend did not return a jobId');
      setCascadeJobId(jobId);
    } catch (e) {
      setProgressPhase('error');
      setSubmitBusy(false);
      toast.error(e.response?.data?.detail || e.message || 'Failed to start cascade job');
      return;
    }

    let lastLogLen = 0;
    // Poll until done. Errors on a single poll are tolerated (transient
    // network blips) — only a final `error` status aborts.
    let pollErrors = 0;
    return new Promise((resolve) => {
      const finalize = async (final) => {
        if (!final || final.status === 'error') {
          setProgressPhase('error');
          setSubmitBusy(false);
          toast.error(final?.error || 'Cascade failed');
          resolve();
          return;
        }
        if (final.status === 'cancelled') {
          setProgressPhase('cancelled');
          setSubmitBusy(false);
          setCancelling(false);
          setCascadeFailures(final.cascadeErrors || []);
          toast.info(`Cancelled · ${final.approved || 0} approved · ${final.cascaded || 0} cascaded before stop.`);
          resolve();
          return;
        }
        setCascadeFailures(final.cascadeErrors || []);
        toast.success(`${final.approved} approved · ${final.rejected} rejected${final.cascaded ? ` · Cognara sequenced ${final.cascaded} tag${final.cascaded === 1 ? '' : 's'}` : ''}`);
        if ((final.cascadeErrors || []).length) {
          toast.warning(`${final.cascadeErrors.length} cascade failure${final.cascadeErrors.length === 1 ? '' : 's'} — use "Resume from tag" to retry.`);
          setSubmitBusy(false);
          resolve();
          return;
        }
        // All good — load preview.
        const approvedTags = rows.filter(r => r.decision === 'APPROVE').map(r => r.structuralTag);
        if (!approvedTags.length) { onDone(); resolve(); return; }
        try {
          const plans = await Promise.all(approvedTags.map(t =>
            api.get(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(t)}/plan`)
              .then(r => ({ ok: true, tag: t, data: r.data }))
              .catch(() => ({ ok: false, tag: t }))
          ));
          const previewPayload = plans.filter(p => p.ok).map(p => ({
            structuralTag: p.tag,
            tagDoc:        p.data.tag,
            marks:         (p.data.marks || []).filter(m => m.cognaraStatus === 'SUGGESTED'),
            rationale:     p.data.tag?.cognaraSequenceRationale,
          })).filter(p => p.marks.length > 0);
          if (!previewPayload.length) {
            toast.info('No mark-level suggestions to review — approved tags had no marks or Cognara returned empty.');
            onDone(); resolve(); return;
          }
          const seed = {};
          previewPayload.forEach(p => p.marks.forEach(m => { seed[`${p.structuralTag}::${m.markNumber}`] = { decision: 'APPROVE' }; }));
          setMarkDecisions(seed);
          setPreviewTags(previewPayload);
          setPhase('preview');
        } finally { setSubmitBusy(false); resolve(); }
      };

      const poll = async () => {
        try {
          const { data: job } = await api.get(
            `/v1/projects/${projectId}/swm/cognara/tags/approve-tag-links/status/${jobId}`,
          );
          pollErrors = 0;
          setProgressPhase(job.phase || job.status);
          if (job.message) setProgressMsg(job.message);
          if (typeof job.decisionsProcessed === 'number') setCascadeProcessed(job.decisionsProcessed);
          if (typeof job.decisionsTotal === 'number') setCascadeTotal(job.decisionsTotal);
          const log = job.log || [];
          if (log.length > lastLogLen) {
            const additions = log.slice(lastLogLen);
            lastLogLen = log.length;
            setCascadeLog(prev => [...prev, ...additions]);
          }
          if (job.status === 'done' || job.status === 'error' || job.status === 'cancelled') {
            await finalize(job);
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          pollErrors += 1;
          if (pollErrors > 5) {
            setProgressPhase('error');
            setSubmitBusy(false);
            toast.error(e.response?.data?.detail || e.message || 'Cascade poll failed');
            resolve();
            return;
          }
          setTimeout(poll, 3000);
        }
      };
      poll();
    });
  };

  /**
   * Step B — Commit Cognara-suggested mark dates into `plannedFabStart` etc.
   *
   * Calls `approve-plan` per tag with the human's per-mark decisions + edits.
   * Rejected marks get `cognaraStatus=REJECTED` and are NOT written to plannedX.
   */
  const commit = async () => {
    setCommitBusy(true);
    try {
      let totalApproved = 0, totalRejected = 0;
      for (const p of previewTags) {
        const rows = p.marks.map(m => {
          const d = markDecisions[`${p.structuralTag}::${m.markNumber}`] || { decision: 'APPROVE' };
          return { markNumber: m.markNumber, decision: d.decision, edits: d.edits };
        });
        if (!rows.length) continue;
        const { data } = await api.post(
          `/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(p.structuralTag)}/approve-plan`,
          { decisions: rows });
        totalApproved += data.approved || 0;
        totalRejected += data.rejected || 0;
      }
      toast.success(`Committed · ${totalApproved} mark${totalApproved === 1 ? '' : 's'} approved · ${totalRejected} rejected`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Commit failed'); }
    finally { setCommitBusy(false); }
  };

  const setMarkDecision = (key, decision) =>
    setMarkDecisions(prev => ({ ...prev, [key]: { decision, edits: prev[key]?.edits } }));
  const setMarkEdit = (key, field, value) =>
    setMarkDecisions(prev => ({
      ...prev,
      [key]: { decision: prev[key]?.decision || 'APPROVE', edits: { ...(prev[key]?.edits || {}), [field]: value } },
    }));
  const approveAllMarks = () => {
    const next = {};
    previewTags.forEach(p => p.marks.forEach(m => { next[`${p.structuralTag}::${m.markNumber}`] = { decision: 'APPROVE' }; }));
    setMarkDecisions(next);
  };

  const totalMarks = previewTags.reduce((n, p) => n + p.marks.length, 0);
  const approvedMarks = Object.values(markDecisions).filter(d => d.decision === 'APPROVE').length;
  const rejectedMarks = Object.values(markDecisions).filter(d => d.decision === 'REJECT').length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-tag-cognara-linker">
      <div className="w-full max-w-6xl max-h-[92vh] rounded-xl overflow-hidden shadow-2xl flex flex-col" style={{ background: 'var(--card-bg)' }}>
        <div className="p-4 border-b flex items-start justify-between gap-3" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono mb-0.5" style={{ color: '#F26B1F' }}>
              {phase === 'link' ? 'Step 1 of 2 · Approve tag → activity match' : 'Step 2 of 2 · Preview cascade before committing to marks'}
            </div>
            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {phase === 'link'
                ? <>Cognara tag → activity linkage · {selectedTags.length} tag{selectedTags.length === 1 ? '' : 's'}</>
                : <>Cascade preview · {totalMarks} mark{totalMarks === 1 ? '' : 's'} across {previewTags.length} tag{previewTags.length === 1 ? '' : 's'}</>}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {phase === 'link'
                ? 'Cognara matches each tag to its best-fit SO-97.16 activity. Approving here writes the activity onto the tag AND runs Cognara mark-sequencing (dates stay in SUGGESTED state until you commit in Step 2).'
                : 'Review the fab/erect dates Cognara proposed per mark. Nothing is written to plannedFabStart/plannedErectStart until you click Commit.'}
            </div>
          </div>
          {phase === 'preview' && (
            <div className="flex items-center gap-2 text-[10px] font-mono shrink-0">
              <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(14,124,107,0.12)', color: '#0E7C6B' }}>{approvedMarks} approve</span>
              <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>{rejectedMarks} reject</span>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-3">
          {phase === 'link' ? (
            <>
              {(busy || submitBusy || ['queued','loading_activities','activities_loaded','loading_tags','analyzing','streaming','starting','tag_start','tag_linked','cascade_start','cascade_done','cancelled','error'].includes(progressPhase)) && progressPhase !== 'done' && (
                <div className="rounded ring-1 p-3 space-y-2" style={{ background: 'rgba(242,107,31,0.06)', borderColor: 'rgba(242,107,31,0.35)' }} data-testid="swm-cognara-progress">
                  <div className="flex items-center gap-2 text-[11.5px]" style={{ color: 'var(--text-primary)' }}>
                    {progressPhase === 'error' ? (
                      <XCircle className="w-4 h-4" style={{ color: '#EF4444' }} />
                    ) : progressPhase === 'cancelled' ? (
                      <XCircle className="w-4 h-4" style={{ color: '#F26B1F' }} />
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#F26B1F' }} />
                    )}
                    <b>{submitBusy
                        ? (cancelling ? 'Cancelling cascade — finishing current tag…' : 'Cognara sequencing marks for each approved tag')
                        : 'Cognara analysing tags against the SO-97.16 hierarchy'}</b>
                    <span className="ml-auto text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                      {elapsedSec}s elapsed · phase: {progressPhase}
                    </span>
                  </div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{progressMsg || 'Reading tag geometry and matching against activities…'}</div>

                  {submitBusy && cascadeTotal > 0 && (
                    <div className="space-y-1" data-testid="swm-cascade-progress-bar">
                      <div className="flex items-center justify-between text-[10.5px] font-mono" style={{ color: 'var(--text-primary)' }}>
                        <span>{cascadeProcessed} / {cascadeTotal} tags · {Math.round((cascadeProcessed / Math.max(cascadeTotal, 1)) * 100)}% complete</span>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {cascadeProcessed > 0 && cascadeProcessed < cascadeTotal
                            ? `~${Math.max(1, Math.round((elapsedSec / Math.max(cascadeProcessed, 1)) * (cascadeTotal - cascadeProcessed)))}s remaining`
                            : cascadeProcessed >= cascadeTotal ? 'finalising…' : 'estimating…'}
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(148,163,184,0.15)' }}>
                        <div className="h-full transition-all duration-500 rounded-full"
                          style={{
                            width: `${Math.round((cascadeProcessed / Math.max(cascadeTotal, 1)) * 100)}%`,
                            background: progressPhase === 'cancelled' ? '#F26B1F' : 'linear-gradient(90deg, #F26B1F 0%, #FFB37A 100%)',
                          }} />
                      </div>
                    </div>
                  )}

                  {progressPhase !== 'error' && !submitBusy && (
                    <div className="grid grid-cols-4 gap-1.5">
                      {[
                        { key: 'loading_activities', label: 'Load schedule',
                          done: ['activities_loaded','loading_tags','analyzing','streaming','done'].includes(progressPhase),
                          extra: progressActivityCount ? `${progressActivityCount} acts` : '' },
                        { key: 'loading_tags', label: 'Summarise tags',
                          done: ['analyzing','streaming','done'].includes(progressPhase) || progressTagsProcessed >= selectedTags.length,
                          extra: `${progressTagsProcessed}/${selectedTags.length}` },
                        { key: 'analyzing', label: 'Cognara reasoning',
                          done: progressPhase === 'done',
                          active: ['analyzing','streaming'].includes(progressPhase) },
                        { key: 'streaming', label: 'Stream suggestions',
                          done: progressPhase === 'done',
                          extra: `${suggestions.length}/${selectedTags.length}` },
                      ].map(step => {
                        const active = step.active || progressPhase === step.key;
                        return (
                          <div key={step.key} className="rounded px-2 py-1.5" style={{
                            background: step.done ? 'rgba(14,124,107,0.10)'
                                       : active ? 'rgba(242,107,31,0.12)'
                                       : 'rgba(148,163,184,0.08)',
                            border: `1px solid ${step.done ? 'rgba(14,124,107,0.35)' : active ? 'rgba(242,107,31,0.35)' : 'transparent'}`,
                          }}>
                            <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider" style={{ color: step.done ? '#0E7C6B' : active ? '#F26B1F' : 'var(--text-muted)' }}>
                              {step.done ? <Check className="w-2.5 h-2.5" strokeWidth={3}/>
                                         : active ? <Loader2 className="w-2.5 h-2.5 animate-spin"/>
                                         : <span className="w-2.5 h-2.5 inline-block rounded-full border" style={{ borderColor: 'currentColor' }}/>}
                              <span className="truncate">{step.label}</span>
                            </div>
                            {step.extra && (
                              <div className="text-[10.5px] font-mono mt-0.5" style={{ color: 'var(--text-primary)' }}>{step.extra}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {progressPhase === 'error' && (
                    <div className="text-[10.5px]" style={{ color: '#EF4444' }}>Analysis failed. Close this dialog and try again — Cognara might be rate-limited or the schedule may not be linked.</div>
                  )}
                  {progressPhase === 'cancelled' && (
                    <div className="text-[10.5px]" style={{ color: '#F26B1F' }}>Cascade cancelled by user. Tags processed before cancellation kept their links.</div>
                  )}
                  {submitBusy && cascadeLog.length > 0 && (
                    <div className="rounded p-1.5 max-h-32 overflow-y-auto text-[10px] font-mono space-y-0.5"
                      style={{ background: 'rgba(20,58,107,0.05)', border: '1px solid rgba(20,58,107,0.15)' }}
                      data-testid="swm-cascade-log">
                      {cascadeLog.slice(-30).map((ev, i) => {
                        const isErr = ev.phase === 'tag_error' || ev.phase === 'cascade_error';
                        const isDone = ev.phase === 'tag_linked' || ev.phase === 'cascade_done' || ev.phase === 'tag_rejected';
                        const isActive = ev.phase === 'tag_start' || ev.phase === 'cascade_start';
                        const label = ev.phase === 'tag_start'      ? `→ ${ev.structuralTag} · linking (${ev.index}/${ev.total})`
                                    : ev.phase === 'tag_linked'     ? `✓ ${ev.structuralTag} · ${ev.activityWBS || ''} ${ev.activityName || ''}${ev.priority ? ` · P${ev.priority}` : ''}`
                                    : ev.phase === 'tag_rejected'   ? `✕ ${ev.structuralTag} · rejected`
                                    : ev.phase === 'cascade_start'  ? `⋯ ${ev.structuralTag} · Cognara sequencing marks…`
                                    : ev.phase === 'cascade_done'   ? `✓ ${ev.structuralTag} · sequenced ${ev.markCount || 0} marks`
                                    : ev.phase === 'cascade_error'  ? `⚠ ${ev.structuralTag} · ${ev.error}`
                                    : ev.phase === 'tag_error'      ? `⚠ ${ev.structuralTag} · ${ev.error}`
                                    : ev.message || JSON.stringify(ev);
                        return (
                          <div key={i} style={{ color: isErr ? '#EF4444' : isDone ? '#0E7C6B' : isActive ? '#F26B1F' : 'var(--text-muted)' }}>{label}</div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {rationale && (
                <div className="p-2.5 rounded ring-1 text-[11px]" style={{ background: 'rgba(242,107,31,0.08)', borderColor: 'rgba(242,107,31,0.4)', color: 'var(--text-primary)' }}>
                  <b>Cognara rationale:</b> {rationale}
                </div>
              )}
              {cascadeFailures.length > 0 && (
                <div className="p-2.5 rounded ring-1 space-y-1.5" style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.4)' }} data-testid="swm-cascade-failures">
                  <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: '#EF4444' }}>
                    <XCircle className="w-3.5 h-3.5" /> {cascadeFailures.length} tag{cascadeFailures.length === 1 ? '' : 's'} failed during cascade — resume any of them to retry from that point without re-running the whole batch.
                  </div>
                  <div className="space-y-1">
                    {cascadeFailures.map(f => (
                      <div key={f.structuralTag} className="flex items-center gap-2 text-[10.5px] font-mono">
                        <span className="font-mono" style={{ color: '#EF4444' }}>✕ {f.structuralTag}</span>
                        <span className="italic truncate" style={{ color: 'var(--text-muted)' }} title={f.error}>{f.error}</span>
                        <button type="button" onClick={() => retryCascadeFrom(f.structuralTag)}
                          disabled={submitBusy}
                          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border disabled:opacity-50"
                          style={{ borderColor: '#F26B1F', color: '#F26B1F' }}
                          data-testid={`swm-cascade-retry-${f.structuralTag}`}>
                          <Sparkles className="w-2.5 h-2.5" /> Resume from here
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[11px]">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr style={{ color: 'var(--text-muted)' }}>
                      <th className="p-1.5 text-left font-medium">Tag</th>
                      <th className="p-1.5 text-left font-medium">Suggested activity</th>
                      <th className="p-1.5 text-center font-medium">P</th>
                      <th className="p-1.5 text-left font-medium">Reason</th>
                      <th className="p-1.5 text-center font-medium">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map(s => {
                      const d = decisions[s.structuralTag] || {};
                      return (
                        <tr key={s.structuralTag} className="border-b" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-tag-cognara-row-${s.structuralTag}`}>
                          <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{s.structuralTag}</td>
                          <td className="p-1.5">
                            <select value={d.activityId || ''}
                              onChange={e => setDecisions(prev => ({ ...prev, [s.structuralTag]: { decision: 'APPROVE', activityId: e.target.value } }))}
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-transparent w-full"
                              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                              <option value="">(pick activity)</option>
                              {activities.map(a => (
                                <option key={a.id} value={a.id}
                                  disabled={a.isSummary || a.isMilestone}
                                  style={{ fontWeight: a.isSummary ? 700 : 400,
                                           color: a.isSummary ? '#143A6B' : undefined,
                                           background: a.isSummary ? 'rgba(20,58,107,0.05)' : undefined }}>
                                  {'\u00a0\u00a0'.repeat(a.level || 0)}{a.wbs} · {a.name}{a.isSummary ? '  ▸' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-1.5 text-center">
                            {s.priority ? (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[9.5px] font-bold font-mono"
                                style={{
                                  background: `${['','#ef4444','#f59e0b','#3b82f6','#143A6B','#94a3b8'][s.priority] || '#94a3b8'}22`,
                                  color: ['','#ef4444','#f59e0b','#3b82f6','#143A6B','#94a3b8'][s.priority] || '#94a3b8',
                                }}
                                title="Cognara-assigned priority: P1 = most critical, P5 = least critical">
                                P{s.priority}
                              </span>
                            ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                          <td className="p-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{s.rationale}</td>
                          <td className="p-1.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button type="button"
                                onClick={() => setDecisions(prev => ({ ...prev, [s.structuralTag]: { decision: 'APPROVE', activityId: prev[s.structuralTag]?.activityId || s.activityId } }))}
                                className="w-6 h-6 rounded flex items-center justify-center cursor-pointer"
                                style={{ background: d.decision === 'APPROVE' ? '#0E7C6B' : 'transparent', color: d.decision === 'APPROVE' ? '#fff' : '#0E7C6B', border: '1px solid #0E7C6B' }}
                                title="Approve" data-testid={`swm-tag-cognara-approve-${s.structuralTag}`}>
                                <Check className="w-3 h-3" strokeWidth={3} />
                              </button>
                              <button type="button"
                                onClick={() => setDecisions(prev => ({ ...prev, [s.structuralTag]: { decision: 'REJECT', activityId: null } }))}
                                className="w-6 h-6 rounded flex items-center justify-center cursor-pointer"
                                style={{ background: d.decision === 'REJECT' ? '#EF4444' : 'transparent', color: d.decision === 'REJECT' ? '#fff' : '#EF4444', border: '1px solid #EF4444' }}
                                title="Reject" data-testid={`swm-tag-cognara-reject-${s.structuralTag}`}>
                                <XCircle className="w-3 h-3" strokeWidth={2.5} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!busy && suggestions.length === 0 && (
                      <tr><td colSpan={4} className="p-4 text-center text-[11px] italic" style={{ color: 'var(--text-muted)' }}>
                        No suggestions yet. Cognara might still be thinking, or the response was unparseable — click Cancel and retry.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="p-2.5 rounded ring-1 text-[11px] flex items-start gap-2" style={{ background: 'rgba(20,58,107,0.08)', borderColor: 'rgba(20,58,107,0.35)', color: 'var(--text-primary)' }}>
                <CalendarCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#143A6B' }} />
                <div>
                  <b>Human-in-the-loop cascade.</b> Cognara sequenced marks by weight → elevation → section. Approve/reject each row (or edit dates) — no plannedFabStart / plannedErectStart is written until you click <b>Commit dates to marks</b>.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={approveAllMarks}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border"
                  style={{ borderColor: '#0E7C6B', color: '#0E7C6B' }}
                  data-testid="swm-cascade-approve-all">
                  <Check className="w-3 h-3" /> Approve all
                </button>
                <span className="text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
                  {approvedMarks} of {totalMarks} marks queued for commit · {rejectedMarks} rejected
                </span>
              </div>
              {previewTags.map(p => (
                <div key={p.structuralTag} className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-cascade-tag-${p.structuralTag}`}>
                  <div className="px-3 py-1.5 flex items-center gap-2 text-[11px]" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)' }}>
                    <Sparkles className="w-3 h-3" style={{ color: '#F26B1F' }} />
                    <span className="font-mono font-semibold">{p.structuralTag}</span>
                    <span style={{ color: 'var(--text-muted)' }}>→ {p.tagDoc?.linkedMacroActivityWBS} · {p.tagDoc?.linkedMacroActivityName}</span>
                    <span className="ml-auto text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                      {p.tagDoc?.linkedMacroActivityStart?.slice(0, 10)} → {p.tagDoc?.linkedMacroActivityFinish?.slice(0, 10)} · {p.marks.length} marks
                    </span>
                  </div>
                  {p.rationale && (
                    <div className="px-3 py-1.5 text-[10.5px] border-b" style={{ borderColor: 'var(--border-color)', background: 'rgba(242,107,31,0.05)', color: 'var(--text-primary)' }}>
                      <b>Sequencing logic:</b> {p.rationale}
                    </div>
                  )}
                  <table className="w-full text-[10.5px]">
                    <thead style={{ background: 'var(--card-bg)' }}>
                      <tr style={{ color: 'var(--text-muted)' }}>
                        <th className="p-1.5 text-left font-medium">#</th>
                        <th className="p-1.5 text-left font-medium">Mark</th>
                        <th className="p-1.5 text-left font-medium">Fab Start</th>
                        <th className="p-1.5 text-left font-medium">Fab Finish</th>
                        <th className="p-1.5 text-left font-medium">Erect Start</th>
                        <th className="p-1.5 text-left font-medium">Erect Finish</th>
                        <th className="p-1.5 text-center font-medium">P</th>
                        <th className="p-1.5 text-left font-medium" style={{ minWidth: 180 }}>Cascade timeline</th>
                        <th className="p-1.5 text-left font-medium">Rationale</th>
                        <th className="p-1.5 text-center font-medium">Decision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.marks.map(m => {
                        const key = `${p.structuralTag}::${m.markNumber}`;
                        const d = markDecisions[key] || { decision: 'APPROVE' };
                        const fs = d.edits?.plannedFabStart    ?? m.cognaraSuggestedFabStart;
                        const ff = d.edits?.plannedFabFinish   ?? m.cognaraSuggestedFabFinish;
                        const es = d.edits?.plannedErectStart  ?? m.cognaraSuggestedErectStart;
                        const ef = d.edits?.plannedErectFinish ?? m.cognaraSuggestedErectFinish;
                        const rejected = d.decision === 'REJECT';
                        return (
                          <tr key={m.markNumber} className="border-b" style={{ borderColor: 'var(--border-color)', background: rejected ? 'rgba(239,68,68,0.06)' : undefined }}
                            data-testid={`swm-cascade-mark-${p.structuralTag}-${m.markNumber}`}>
                            <td className="p-1.5 text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{m.cognaraSequenceIdx || '-'}</td>
                            <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{m.markNumber}</td>
                            <td className="p-1.5"><input type="date" value={fs || ''} disabled={rejected}
                              onChange={e => setMarkEdit(key, 'plannedFabStart', e.target.value)}
                              className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                            <td className="p-1.5"><input type="date" value={ff || ''} disabled={rejected}
                              onChange={e => setMarkEdit(key, 'plannedFabFinish', e.target.value)}
                              className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                            <td className="p-1.5"><input type="date" value={es || ''} disabled={rejected}
                              onChange={e => setMarkEdit(key, 'plannedErectStart', e.target.value)}
                              className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                            <td className="p-1.5"><input type="date" value={ef || ''} disabled={rejected}
                              onChange={e => setMarkEdit(key, 'plannedErectFinish', e.target.value)}
                              className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                            <td className="p-1.5 text-center font-mono">{m.cognaraSuggestedPriority || '-'}</td>
                            <td className="p-1.5" style={{ minWidth: 180 }}>
                              <CascadeTimelineStrip
                                macroStart={p.tagDoc?.linkedMacroActivityStart}
                                macroFinish={p.tagDoc?.linkedMacroActivityFinish}
                                fabStart={fs} fabFinish={ff}
                                erectStart={es} erectFinish={ef}
                                rejected={rejected}
                              />
                            </td>
                            <td className="p-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{m.cognaraRationale}</td>
                            <td className="p-1.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button type="button" onClick={() => setMarkDecision(key, 'APPROVE')}
                                  className="w-6 h-6 rounded flex items-center justify-center cursor-pointer"
                                  style={{ background: d.decision === 'APPROVE' ? '#0E7C6B' : 'transparent', color: d.decision === 'APPROVE' ? '#fff' : '#0E7C6B', border: '1px solid #0E7C6B' }}
                                  title="Approve" data-testid={`swm-cascade-approve-${p.structuralTag}-${m.markNumber}`}>
                                  <Check className="w-3 h-3" strokeWidth={3} />
                                </button>
                                <button type="button" onClick={() => setMarkDecision(key, 'REJECT')}
                                  className="w-6 h-6 rounded flex items-center justify-center cursor-pointer"
                                  style={{ background: d.decision === 'REJECT' ? '#EF4444' : 'transparent', color: d.decision === 'REJECT' ? '#fff' : '#EF4444', border: '1px solid #EF4444' }}
                                  title="Reject" data-testid={`swm-cascade-reject-${p.structuralTag}-${m.markNumber}`}>
                                  <XCircle className="w-3 h-3" strokeWidth={2.5} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="p-3 border-t flex items-center justify-between gap-2" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
          {phase === 'preview' ? (
            <button type="button" onClick={() => setPhase('link')} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid="swm-cascade-back">
              <ArrowLeft className="w-3 h-3" /> Back to tag decisions
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1 rounded text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid="swm-tag-cognara-cancel">Cancel</button>
            {submitBusy && cascadeJobId && (
              <button type="button" onClick={cancelCascade} disabled={cancelling}
                className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium border disabled:opacity-50"
                style={{ borderColor: '#EF4444', color: '#EF4444' }}
                title="Ask the worker to stop after the current tag — already-processed tags stay linked."
                data-testid="swm-cascade-stop-btn">
                {cancelling ? <Loader2 className="w-3 h-3 animate-spin"/> : <XCircle className="w-3 h-3"/>}
                {cancelling ? 'Stopping…' : 'Stop cascade'}
              </button>
            )}
            {phase === 'link' ? (
              <>
                <button type="button" onClick={saveMappingOnly}
                  disabled={saveMappingBusy || submitBusy || Object.keys(decisions).length === 0}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium border disabled:opacity-50"
                  style={{ borderColor: '#143A6B', color: '#143A6B' }}
                  title="Persist the tag→activity mapping without running the mark cascade"
                  data-testid="swm-tag-cognara-save-mapping">
                  {saveMappingBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Link2 className="w-3 h-3"/>} Save mapping ({Object.keys(decisions).length})
                </button>
                <button type="button" onClick={submit} disabled={submitBusy || saveMappingBusy || Object.keys(decisions).length === 0}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium text-white disabled:opacity-50"
                  style={{ background: '#F26B1F' }}
                  data-testid="swm-tag-cognara-submit">
                  {submitBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} Submit &amp; preview cascade ({Object.keys(decisions).length})
                </button>
              </>
            ) : (
              <button type="button" onClick={commit} disabled={commitBusy || approvedMarks === 0}
                className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium text-white disabled:opacity-50"
                style={{ background: '#0E7C6B' }}
                data-testid="swm-cascade-commit">
                {commitBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <CalendarCheck className="w-3 h-3"/>} Commit dates to marks ({approvedMarks})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
